if (!customElements.get('collection-list')) {
  class CollectionList extends HTMLElement {
    constructor() {
      super();
      this.collectionCountCache = new Map();
      this.resolvedCollectionCounts = new Map();
    }

    connectedCallback() {
      this.selectors = {
        sliderWrapper: '.collection-list__items',
        pagination: '.swiper-pagination',
        nextEl: '.swiper-btn-next',
        prevEl: '.swiper-btn-prev',
      };
      this.classes = {
        grid: 'f-grid',
        swiper: 'swiper',
        swiperWrapper: 'swiper-wrapper',
      };

      this.sectionId = this.dataset.sectionId;
      this.section = this.closest(`.section-${this.sectionId}`);
      this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);
      this.updateCollectionCounts();

      this.enableSlider = this.dataset.enableSlider === 'true';
      this.items = parseInt(this.dataset.items);
      this.tabletItems = parseInt(this.dataset.tabletItems);

      this.sliderInstance = false;

      if (!this.enableSlider) return;

      this.init();
      document.addEventListener('matchMobile', () => {
        this.init();
      });
      document.addEventListener('unmatchMobile', () => {
        this.init();
      });
    }

    init() {
      if (FoxTheme.config.mqlMobile) {
        this.destroySlider();
      } else {
        this.initSlider();
      }
    }

    initSlider() {
      const columnGap = FoxTheme.utils.getGridColumnGap(this.sliderWrapper);

      const sliderOptions = {
        slidesPerView: this.tabletItems > 3 ? 3 : parseInt(this.tabletItems),
        spaceBetween: columnGap.tablet,
        navigation: {
          nextEl: this.section.querySelector(this.selectors.nextEl),
          prevEl: this.section.querySelector(this.selectors.prevEl),
        },
        pagination: false,
        breakpoints: {
          1024: {
            slidesPerView: parseInt(this.items),
            spaceBetween: columnGap.tabletLarge
          },
          1280: {
            slidesPerView: parseInt(this.items),
            spaceBetween: columnGap.desktop
          },
        },
        loop: true,
        threshold: 2,
      };

      if (typeof this.sliderInstance !== 'object') {
        this.classList.add(this.classes.swiper);
        this.sliderWrapper.classList.remove(this.classes.grid);
        this.sliderWrapper.classList.add(this.classes.swiperWrapper);
        this.sliderInstance = new window.FoxTheme.Carousel(this, sliderOptions);
        this.sliderInstance.init();
        this.updateCollectionCounts();

        const focusableElements = FoxTheme.a11y.getFocusableElements(this);

        focusableElements.forEach((element) => {
          element.addEventListener('focusin', () => {
            const slide = element.closest('.swiper-slide');
            this.sliderInstance.slider.slideTo(this.sliderInstance.slider.slides.indexOf(slide));
          });
        });
      }

      if (Shopify.designMode && typeof this.sliderInstance === 'object') {
        document.addEventListener('shopify:block:select', (e) => {
          if (e.detail.sectionId != this.sectionId) return;
          let { target } = e;
          const index = Number(target.dataset.index);

          this.sliderInstance.slider.slideToLoop(index);
        });
      }
    }

    updateCollectionCounts() {
      const countElements = this.getCountElements();
      if (!countElements.length) return;

      countElements.forEach((countElement) => {
        const collectionUrl = countElement.dataset.collectionUrl;
        if (!collectionUrl) return;

        this.getExpandedCollectionCount(collectionUrl).then((expandedCount) => {
          if (!Number.isFinite(expandedCount)) return;

          this.resolvedCollectionCounts.set(collectionUrl, expandedCount);
          this.applyCountToCollectionElements(collectionUrl, expandedCount);
        });
      });
    }

    getExpandedCollectionCount(collectionUrl) {
      if (!this.collectionCountCache.has(collectionUrl)) {
        this.collectionCountCache.set(collectionUrl, this.fetchExpandedCollectionCount(collectionUrl));
      }
      return this.collectionCountCache.get(collectionUrl);
    }

    async fetchExpandedCollectionCount(collectionUrl) {
      const firstPageUrl = new URL(collectionUrl, window.location.origin);
      firstPageUrl.searchParams.delete('page');

      const firstPageDocument = await this.fetchCollectionDocument(firstPageUrl.toString());
      if (!firstPageDocument) return NaN;

      const firstPageMeta = this.extractExpandedCountMeta(firstPageDocument);
      if (!firstPageMeta) return NaN;

      let totalCount = firstPageMeta.currentPageCount;
      const totalPages = firstPageMeta.totalPages;

      if (totalPages <= 1) return totalCount;

      const pageFetches = [];
      for (let page = 2; page <= totalPages; page += 1) {
        const nextPageUrl = new URL(firstPageUrl.toString());
        nextPageUrl.searchParams.set('page', `${page}`);
        pageFetches.push(
          this.fetchCollectionDocument(nextPageUrl.toString()).then((pageDocument) => {
            const pageMeta = this.extractExpandedCountMeta(pageDocument);
            return pageMeta ? pageMeta.currentPageCount : 0;
          })
        );
      }

      const pageCounts = await Promise.all(pageFetches);
      pageCounts.forEach((pageCount) => {
        if (Number.isFinite(pageCount)) totalCount += pageCount;
      });

      return totalCount;
    }

    getCountElements() {
      return Array.from(this.querySelectorAll('.collection-card__count[data-collection-url]'));
    }

    applyCountToCollectionElements(collectionUrl, expandedCount) {
      const countElements = this.getCountElements().filter(
        (countElement) => countElement.dataset.collectionUrl === collectionUrl
      );
      if (!countElements.length) return;

      countElements.forEach((countElement) => {
        const template = countElement.dataset.countTemplate || '__count__';
        countElement.textContent = template.replaceAll('__count__', `${expandedCount}`);
      });
    }

    async fetchCollectionDocument(url) {
      try {
        const response = await fetch(url);
        const responseText = await response.text();
        return new DOMParser().parseFromString(responseText, 'text/html');
      } catch (error) {
        return null;
      }
    }

    extractExpandedCountMeta(collectionDocument) {
      if (!collectionDocument) return null;

      const expandedMetaElement = collectionDocument.querySelector('[data-expanded-card-count-meta]');
      const productCountElement = collectionDocument.getElementById('ProductCount');
      const sourceElement = expandedMetaElement || productCountElement;

      if (sourceElement) {
        const currentPageCount = parseInt(sourceElement.dataset.cardCountCurrentPage || '0', 10);
        const totalPages = parseInt(sourceElement.dataset.totalPages || '1', 10);
        if (Number.isFinite(currentPageCount)) {
          return {
            currentPageCount,
            totalPages: Number.isFinite(totalPages) && totalPages > 0 ? totalPages : 1,
          };
        }
      }

      const productsList = collectionDocument.getElementById('ProductsList');
      const currentPageCount = productsList ? productsList.querySelectorAll('.f-column.card').length : 0;
      const totalPages = this.getTotalPagesFromPagination(collectionDocument);

      return {
        currentPageCount,
        totalPages,
      };
    }

    getTotalPagesFromPagination(collectionDocument) {
      let totalPages = 1;
      const paginationItems = collectionDocument.querySelectorAll('.pagination__list .pagination__item');

      paginationItems.forEach((item) => {
        const pageNumber = parseInt(item.textContent.trim(), 10);
        if (Number.isFinite(pageNumber) && pageNumber > totalPages) {
          totalPages = pageNumber;
        }
      });

      return totalPages;
    }

    destroySlider() {
      this.classList.remove(this.classes.swiper);
      this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
      this.sliderWrapper.classList.add(this.classes.grid);
      if (typeof this.sliderInstance === 'object') {
        this.sliderInstance.slider.destroy();
        this.sliderInstance = false;
      }
    }
  }
  customElements.define('collection-list', CollectionList);
}
